
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LayoutDashboard } from 'lucide-react';
import UploadsList from '@/components/dashboard/UploadsList';
import UploadForm from '@/components/dashboard/UploadForm';
import PromoteTrackButton from './PromoteTrackButton';
import TrackCreatedModal from './TrackCreatedModal';
import { TrackDownload } from '@/lib/types';
import { toast } from "sonner";

interface TracksContentProps {
  isCreating: boolean;
  uploads: TrackDownload[];
  artistId: string | null;
  handleCreateClick: () => void;
  handleCreateSuccess: () => void;
  addUpload: (upload: TrackDownload) => void;
  removeUpload: (trackId: string) => Promise<boolean>;
}

const TracksContent: React.FC<TracksContentProps> = ({
  isCreating,
  uploads,
  artistId,
  handleCreateClick,
  handleCreateSuccess,
  addUpload,
  removeUpload
}) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newTrack, setNewTrack] = useState<TrackDownload | null>(null);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (newTrack && topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [newTrack]);

  const handleUploadSuccess = async (upload: TrackDownload) => {
    addUpload(upload);
    setNewTrack(upload);
    setShowSuccessModal(true);
    handleCreateSuccess();
    toast.success(`Track "${upload.trackName}" uploaded successfully!`);
    
    // Note: Email notification is now handled by useSuccessModal hook in the Dashboard component
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setNewTrack(null);
  };

  return (
    <div className="container px-4 mx-auto">
      <div ref={topRef} className="w-full max-w-4xl mx-auto">
        <div className="flex justify-between mb-6">
          <Link to="/dashboard">
            <Button variant="outline" size="sm" className="gap-2">
              <LayoutDashboard size={16} />
              View Dashboard
            </Button>
          </Link>
        </div>
        
        <h1 className="text-3xl font-bold mb-2">Your Tracks</h1>
        <p className="text-muted-foreground mb-8">
          Manage your Spotify downloads
        </p>
        
        <PromoteTrackButton isCreating={isCreating} onClick={handleCreateClick} />
        
        {!isCreating && (
          <UploadsList 
            uploads={uploads} 
            onCreateClick={handleCreateClick} 
            onDeleteTrack={removeUpload} 
          />
        )}
        
        {isCreating && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Create Track Download</h2>
            <p className="text-muted-foreground mb-4">
              Fill out the form below to create a new track download.
            </p>
            <UploadForm 
              onUploadSuccess={handleUploadSuccess} 
              artistId={artistId} 
              onSuccess={(upload) => {
                if (upload) {
                  handleUploadSuccess(upload);
                } else {
                  handleCreateSuccess();
                }
              }}
            />
          </div>
        )}

        {newTrack && (
          <TrackCreatedModal
            isOpen={showSuccessModal}
            onClose={handleCloseModal}
            trackId={newTrack.id}
            trackName={newTrack.trackName || 'New Track'}
            isSendingNotification={isSendingNotification}
          />
        )}
      </div>
    </div>
  );
};

export default TracksContent;
